import { useCallback, useEffect, useState } from "react";
import api, { nzISODate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CODE = { "Annual leave": "AL", "Sick leave": "SL", "Bereavement leave": "BL", "Public holiday": "PH" };
const first = n => (n || "").split(" ")[0];
const addDays = (iso, k) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + k); return d.toISOString().slice(0, 10); };
const dayLabel = iso => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "UTC" });
const tag = l => `${CODE[l.type] || "Leave"}: ${first(l.worker_name)}${l.part_day ? ` (${l.hours} h)` : ""}`;

function workDays(today) {
  const d = new Date(`${today}T00:00:00Z`); const mon = addDays(today, -((d.getUTCDay() || 7) - 1));
  return Array.from({ length: 14 }, (_, i) => addDays(mon, i)).filter((x, i) => i % 7 < 5);
}

// Admin/Manager only: approved leave and public holidays for this week and next, one tag per person per day.
export default function AwayStrip() {
  const { user } = useAuth();
  const mgmt = ["Admin", "Manager"].includes(user?.role);
  const today = nzISODate();
  const days = workDays(today);
  const [lines, setLines] = useState([]); const [hols, setHols] = useState({});
  const load = useCallback(() => {
    api.get("/leave/lines", { params: { start: days[0], end: days[days.length - 1] } }).then(r => setLines(r.data)).catch(() => setLines([]));
  }, [days[0]]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!mgmt) return undefined;
    load();
    api.get("/public-holidays").then(r => setHols(Object.fromEntries(r.data.map(h => [h.date, h.name])))).catch(() => {});
    window.addEventListener("leave-changed", load); window.addEventListener("focus", load);
    return () => { window.removeEventListener("leave-changed", load); window.removeEventListener("focus", load); };
  }, [mgmt, load]);
  if (!mgmt) return null;
  const by = d => lines.filter(l => l.date === d);
  const todayTags = by(today);
  return (
    <div className="card space-y-2" data-testid="away-strip">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-silver text-sm">Away this week and next</h2>
        <span className={`text-sm ${todayTags.length || hols[today] ? "text-amber-300 font-semibold" : "opacity-60"}`} data-testid="away-today">
          Away today: {hols[today] ? `PH ${hols[today]}` : todayTags.length ? todayTags.map(tag).join(", ") : "nobody"}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-1">
        {days.map(d => (
          <div key={d} className={`rounded border p-1 min-h-[3.5rem] ${d === today ? "border-amber-400" : "border-white/10"} ${d < today ? "opacity-50" : ""}`} data-testid={`away-day-${d}`}>
            <div className="text-[11px] opacity-70">{dayLabel(d)}</div>
            {hols[d] && <div className="text-[11px] text-amber-300 truncate" title={hols[d]} data-testid={`away-ph-${d}`}>PH: {hols[d]}</div>}
            {by(d).map((l, i) => <div key={i} className="text-[11px] truncate" title={`${l.worker_name}: ${l.type}, ${l.hours} h`} data-testid={`away-tag-${d}-${l.worker_id}`}>{tag(l)}</div>)}
          </div>))}
      </div>
    </div>
  );
}
